import { ChatbotDatasource } from "@core/data/datasources/chatbot.datasource";
import { LanguageLocalDatasource } from "@core/data/datasources/language-local.datasource";
import { LanguageRemoteDatasource } from "@core/data/datasources/language-remote.datasource";
import { AxiosHttp } from "@core/data/infrastructures/http/axios-http";
import { AxiosHttpConfig } from "@core/data/infrastructures/http/axios-http-config";
import { IotApiClient } from "@core/data/infrastructures/iot-api-client/iot-api-client";
import { ChatbotMapper } from "@core/data/mappers/chatbot.mapper";
import { LanguageIsoCodeEnumMapper } from "@core/data/mappers/language-iso-code-enum.mapper";
import { LanguageUpdateCurrentMapper } from "@core/data/mappers/language-update-current.mapper";
import { LanguageMapper } from "@core/data/mappers/language.mapper";
import { OrderMapper } from "@core/data/mappers/order.mapper";
import { ChatbotImplRepository } from "@core/data/repositories/chatbot.impl-repository";
import { EventBusImplRepository } from "@core/data/repositories/event-bus.impl-repository";
import { LanguageImplRepository } from "@core/data/repositories/language.impl-repository";
import { DiModuleBuilder } from "@core/di/di-module-builder";
import type {
    Http,
    HttpFactory,
    HttpOptions,
} from "@core/domain/infrastructures/http/http";
import type { ChatbotRepository } from "@core/domain/repositories/chatbot.repository";
import type { LanguageRepository } from "@core/domain/repositories/language.repository";
import { ChatbotWeeklyReportUseCase } from "@core/domain/usecases/chatbot/chatbot-get-weekly-report.usecase";
import { ChatbotMessagesDeleteUseCase } from "@core/domain/usecases/chatbot/chatbot-messages-delete.usecase";
import { ChatbotMessagesGetAllUseCase } from "@core/domain/usecases/chatbot/chatbot-messages-get-all.usecase";
import { ChatbotSendMessageUseCase } from "@core/domain/usecases/chatbot/chatbot-send-message.usecase";
import { LanguageGetAllUseCase } from "@core/domain/usecases/language-get-all.usecase";
import { LanguageGetCurrentUseCase } from "@core/domain/usecases/language-get-curent.usecase";
import { LanguageUpdateUseCase } from "@core/domain/usecases/language-update.usecase";
import { coreTypes } from "@core/root/core-types.di";
import { ContainerModule } from "inversify";

const coreModule = new DiModuleBuilder();

coreModule.registerSubModules(() => ({
    datasources: new ContainerModule(({ bind }) => {
        bind(LanguageRemoteDatasource).toSelf();
        bind(LanguageLocalDatasource).toSelf();
        bind(ChatbotDatasource).toSelf();
    }),
    infrastructure: new ContainerModule(({ bind }) => {
        bind(AxiosHttpConfig).toSelf();
        bind<HttpFactory>(coreTypes.infrastructure.HttpFactory).toFactory(
            (context) =>
                (options?: HttpOptions): Http =>
                    new AxiosHttp(
                        context.get(AxiosHttpConfig),
                        context.get(coreTypes.repositories.EventBusRepository),
                        options,
                    ),
        );
        bind<IotApiClient>(coreTypes.infrastructure.IotApiClient)
            .to(IotApiClient)
            .inSingletonScope();
        // LocalStorage binding moved to platform-specific modules (web/mobile)
    }),
    mappers: new ContainerModule(({ bind }) => {
        bind(ChatbotMapper).toSelf();
        bind(LanguageIsoCodeEnumMapper).toSelf();
        bind(LanguageMapper).toSelf();
        bind(LanguageUpdateCurrentMapper).toSelf();
        bind(OrderMapper).toSelf();
    }),
    repositories: new ContainerModule(({ bind }) => {
        bind(coreTypes.repositories.EventBusRepository).to(
            EventBusImplRepository,
        );
        bind(LanguageImplRepository).toSelf();
        bind<LanguageRepository>(coreTypes.repositories.LanguageRepository).to(
            LanguageImplRepository,
        );
        bind<ChatbotRepository>(coreTypes.repositories.ChatbotRepository).to(
            ChatbotImplRepository,
        );
    }),
    useCases: new ContainerModule(({ bind }) => {
        bind(ChatbotMessagesDeleteUseCase).toSelf();
        bind(ChatbotMessagesGetAllUseCase).toSelf();
        bind(ChatbotSendMessageUseCase).toSelf();
        bind(ChatbotWeeklyReportUseCase).toSelf();
        bind(LanguageGetAllUseCase).toSelf();
        bind(LanguageGetCurrentUseCase).toSelf();
        bind(LanguageUpdateUseCase).toSelf();
    }),
}));

coreModule.registerModule();

export { coreModule };
